// App.js
import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css'; // Ensure this global CSS is imported

// Import components that are needed immediately (critical path)
import Sidebar from './components/Sidebar/Sidebar';
import { Login, ProtectedRoute, ParallaxRedirect, PrismRedirect, CoreDailyReportRedirect, DataScienceLabRedirect, ExternalAppReauth } from './features/auth';
import { Settings } from './features/settings';
import useThemeStore from './stores/themeStore';
import useLanguageStore from './stores/languageStore';
import useSidebarStore from './stores/sidebarStore';
import useAuthStore from './stores/authStore';
import LoadingSpinner from './shared/components/LoadingSpinner';
import ErrorNotification from './components/UI/ErrorNotification';
import { startVersionCheck, stopVersionCheck } from './services/versionCheckService';
// import { useAutoHideScrollbar } from './hooks/useAutoHideScrollbar'; // DISABLED FOR TESTING

// Lazy-loaded ChatInterface for code splitting
const ChatInterface = lazy(() => import('./features/chat').then(module => ({ default: module.ChatInterface })));


/**
 * Main application component.
 * Sets up the overall layout with a sidebar on the left and the main content area.
 * Uses React Router for handling different page views.
 */
function App() {
  const initTheme = useThemeStore((state) => state.initTheme);
  const initLanguage = useLanguageStore((state) => state.initLanguage);
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const { isCollapsed, isHidden } = useSidebarStore();
  // const mainContentRef = useAutoHideScrollbar(3000); // 3 second delay - DISABLED FOR TESTING
  const mainContentRef = useRef(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [versionMismatchNotification, setVersionMismatchNotification] = useState(null);

  // Initialize theme and auth on app startup
  useEffect(() => {
    initTheme();
    initLanguage();
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Version check mechanism - detect backend updates and auto-refresh
  useEffect(() => {
    // Callback when version mismatch is detected
    const handleVersionMismatch = (backendVersion, frontendVersion, willAutoRefresh = true) => {
      console.warn('[App] Version mismatch detected:', {
        backend: backendVersion,
        frontend: frontendVersion,
        willAutoRefresh
      });

      // Show notification to user
      setVersionMismatchNotification(
        willAutoRefresh
          ? `New version available (${backendVersion}). Page will refresh automatically...`
          : `New version available (${backendVersion}). Please refresh the page manually.`
      );
    };

    // Start version check service
    startVersionCheck(handleVersionMismatch);

    // Cleanup: stop version checking when component unmounts
    return () => {
      stopVersionCheck();
    };
  }, []);

  // Auto-refresh access token mechanism to prevent automatic logout
  useEffect(() => {
    const refreshAccessToken = useAuthStore.getState().refreshAccessToken;
    const checkAuth = useAuthStore.getState().checkAuth;

    // Refresh token every 10 minutes (access token expires in 15 minutes)
    // This ensures token is refreshed before expiration
    const refreshInterval = setInterval(async () => {
      // Only refresh if user is authenticated
      if (checkAuth()) {
        try {
          // Use silentFail=true for automatic background refresh
          // This prevents logout on temporary network errors
          await refreshAccessToken(true);
        } catch (error) {
          // Don't logout here - silentFail handles it gracefully
        }
      }
    }, 10 * 60 * 1000); // 10 minutes

    // Cleanup interval on unmount
    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  const handleOpenSettings = () => {
    setIsSettingsOpen(true);
    setIsMobileSidebarOpen(false);
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
  };

  const handleOpenMobileSidebar = () => {
    useSidebarStore.getState().setSidebarCollapsed(false);
    setIsMobileSidebarOpen(true);
  };

  const handleCloseMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  const handleMobileNewChat = () => {
    setIsMobileSidebarOpen(false);
    window.dispatchEvent(new CustomEvent('datacenterai:new-chat'));
  };

  useEffect(() => {
    if (!isMobileSidebarOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileSidebarOpen]);

  return (
    <Router basename="/ai">
      {/* Version mismatch notification - appears on top of everything */}
      {versionMismatchNotification && (
        <ErrorNotification
          error={versionMismatchNotification}
          onDismiss={() => setVersionMismatchNotification(null)}
          autoDismissMs={0} // Don't auto-dismiss, page will refresh automatically
          type="info"
        />
      )}

      <Routes>
        {/* Login route - accessible when not authenticated */}
        <Route path="/login" element={<Login />} />

        {/* Parallax SSO bridge - top-level (NOT wrapped in ProtectedRoute):
            preserves ?return= when bouncing the user through /login */}
        <Route path="/auth/parallax-redirect" element={<ParallaxRedirect />} />

        {/* Prism SSO bridge - same external-app pattern as Parallax */}
        <Route path="/auth/prism-redirect" element={<PrismRedirect />} />

        {/* Core Daily Report SSO bridge - same external-app pattern as Parallax */}
        <Route path="/auth/core-daily-report-redirect" element={<CoreDailyReportRedirect />} />

        {/* Data Science Lab sub-project SSO bridge - same rationale as parallax */}
        <Route path="/auth/data-science-lab-redirect" element={<DataScienceLabRedirect />} />

        {/* Generic external-app reauth bridge for child-app session revocation */}
        <Route path="/auth/external-app-reauth" element={<ExternalAppReauth />} />


        {/* Root path - main application */}
        <Route path="/" element={
          <ProtectedRoute>
            <div className="App-container">
              <div className={`App-content-area ${isCollapsed ? 'sidebar-collapsed' : ''} ${isHidden ? 'sidebar-hidden' : ''} ${isMobileSidebarOpen ? 'mobile-sidebar-open' : ''}`}>
                {!isHidden && (
                  <div className="mobile-app-bar">
                    <button
                      className="mobile-sidebar-toggle"
                      onClick={handleOpenMobileSidebar}
                      aria-label="Open navigation"
                      type="button"
                    >
                      <span className="mobile-sidebar-avatar" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                          <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor"/>
                        </svg>
                      </span>
                      <span className="mobile-sidebar-menu-lines" aria-hidden="true">
                        <span></span>
                        <span></span>
                      </span>
                    </button>
                    <div className="mobile-app-title">Data Intelligence</div>
                    <button
                      className="mobile-new-chat-button"
                      onClick={handleMobileNewChat}
                      aria-label="新建会话"
                      title="新建会话"
                      type="button"
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                )}
                {!isHidden && (
                  <button
                    className="mobile-sidebar-backdrop"
                    onClick={handleCloseMobileSidebar}
                    aria-label="Close navigation"
                    type="button"
                  />
                )}
                {!isHidden && (
                  <div className="app-sidebar-region">
                    <Sidebar onOpenSettings={handleOpenSettings} />
                  </div>
                )}
                {/* Collapse/Expand Drag Bar - Positioned above main content */}
                {!isHidden && (
                  <div className="app-collapse-section">
                    <button
                      className="collapse-drag-bar"
                      onClick={() => useSidebarStore.getState().toggleSidebar()}
                      aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                      <div className="drag-handle">
                        <div className="drag-line"></div>
                        <div className="drag-line"></div>
                        <div className="drag-line"></div>
                      </div>
                    </button>
                  </div>
                )}
                <main className="App-main-content" ref={mainContentRef}>
                  <Suspense fallback={<LoadingSpinner message="Loading..." />}>
                    <ChatInterface />
                  </Suspense>
                </main>
              </div>
              {/* Settings Modal */}
              <Settings isOpen={isSettingsOpen} onClose={handleCloseSettings} />
            </div>
          </ProtectedRoute>
        } />

        {/* Catch-all route - redirect any other path to root */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
